import React from "react";

import {
	ColorPickerField,
	Divider,
	InfoMessage,
	Input,
	TextArea,
} from "@amphore-dev/amphore-lib";
import { Form, Formik } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import { TOnboardingProjectValues } from "@/types";

import { OnboardingStepCard } from "../components/OnboardingStepCard";

interface IProjectStepProps {
	initialValues: TOnboardingProjectValues;
	onBack: () => void;
	onNext: (values: TOnboardingProjectValues) => void;
}

const validationSchema = () =>
	Yup.object().shape({
		name: Yup.string().trim().required(t("forms.required")),
		color: Yup.string().required(t("forms.required")),
	});

/** Required step: tasks cannot exist without a project. */
export const ProjectStep: React.FC<IProjectStepProps> = ({
	initialValues,
	onBack,
	onNext,
}) => {
	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema()}
			onSubmit={(values) => onNext(values)}
		>
			{({ values, errors, touched, setFieldValue, submitForm }) => (
				<OnboardingStepCard
					step="project"
					title={t("onboarding.steps.project.title")}
					intro={t("onboarding.steps.project.intro")}
					onBack={onBack}
					onNext={submitForm}
				>
					<Form className="flex flex-col gap-4">
						<div className="flex flex-wrap items-start gap-4">
							<Input
								name="name"
								label={t("projects.namePlaceholder")}
								placeholder={t("projects.namePlaceholder")}
								value={values.name}
								onChange={(value) =>
									setFieldValue("name", value)
								}
								error={touched.name ? errors.name : undefined}
								required
								wrapperClassName="flex-1 min-w-[240px]"
							/>
							<ColorPickerField
								label={t("onboarding.color")}
								value={values.color}
								onChange={(color: string) =>
									setFieldValue("color", color)
								}
							/>
						</div>
						<TextArea
							name="description"
							label={t("tasks.description")}
							placeholder={t("forms.optional")}
							value={values.description}
							onChange={(value) =>
								setFieldValue("description", value)
							}
							rows={3}
						/>
						<Divider
							label={t("onboarding.steps.project.clientSection")}
						/>
						<Input
							name="clientName"
							label={t("onboarding.steps.project.clientName")}
							placeholder={t(
								"onboarding.steps.project.clientPlaceholder"
							)}
							value={values.clientName}
							onChange={(value) =>
								setFieldValue("clientName", value)
							}
							autoComplete="organization"
						/>
						<InfoMessage color="info">
							{t("onboarding.steps.project.clientHint")}
						</InfoMessage>
					</Form>
				</OnboardingStepCard>
			)}
		</Formik>
	);
};
